import { ReactNode } from 'react';
import { Navigate } from 'react-router-dom';
import { useUserRole } from '@/hooks/use-queries';
import { LoadingFallback } from './LoadingFallback';
import { Card, CardContent, CardDescription, CardHeader, CardTitle } from '@/components/ui/card';
import { ShieldAlert } from 'lucide-react';
import { Button } from '@/components/ui/button';
import { useNavigate } from 'react-router-dom';

interface RoleProtectedRouteProps {
  children: ReactNode;
  allowedRoles: ('owner' | 'admin' | 'editor' | 'viewer')[];
  fallbackRoute?: string;
}

/**
 * RoleProtectedRoute - Restricts access to routes based on user role
 * 
 * Wraps routes that require specific roles (e.g., owner/admin only)
 * Shows friendly "Access Denied" message for unauthorized users
 * 
 * Example: <RoleProtectedRoute allowedRoles={['owner', 'admin']}>...</RoleProtectedRoute>
 */
export function RoleProtectedRoute({ 
  children, 
  allowedRoles, 
  fallbackRoute = '/dashboard' 
}: RoleProtectedRouteProps) {
  const { role, loading } = useUserRole();
  const navigate = useNavigate();

  if (loading) {
    return <LoadingFallback />;
  }

  if (!role || !allowedRoles.includes(role)) {
    return (
      <div className="min-h-screen flex items-center justify-center p-4">
        <Card className="max-w-md w-full">
          <CardHeader>
            <div className="flex items-center gap-3 mb-2">
              <div className="w-12 h-12 rounded-full bg-destructive/10 flex items-center justify-center">
                <ShieldAlert className="w-6 h-6 text-destructive" />
              </div>
              <div>
                <CardTitle>Access Restricted</CardTitle>
                <CardDescription>Admin permissions required</CardDescription>
              </div>
            </div>
          </CardHeader>
          <CardContent className="space-y-4">
            <p className="text-sm text-muted-foreground">
              This page requires <span className="font-semibold">
                {allowedRoles.includes('owner') && 'owner'}
                {allowedRoles.includes('admin') && allowedRoles.includes('owner') && ' or '}
                {allowedRoles.includes('admin') && !allowedRoles.includes('owner') && 'admin'}
              </span> access.
            </p>
            <p className="text-sm text-muted-foreground">
              Your current role is <span className="font-semibold">{role}</span>.
              {(role === 'editor' || role === 'viewer') && 
                ' Contact your organization admin to request elevated permissions.'
              }
            </p>
            <Button 
              onClick={() => navigate(fallbackRoute)}
              className="w-full"
            >
              Go to Dashboard
            </Button>
          </CardContent>
        </Card>
      </div>
    );
  }

  return <>{children}</>;
}
